
import { Component, Prop, Vue } from "vue-property-decorator";
import BANNER_LINE_SMALL from "@/assets/images/V2/banner-line-small.png";
import BANNER_LINE_BIG_SHORT from "@/assets/images/V2/banner-line_big-short.png";
import BANNER_LINE_BIG_SHORT_DARK from "@/assets/images/V2/banner-line_big-short_dark.png";
import BANNER_LINE_BIG from "@/assets/images/V2/banner-line_big.png";
import BANNER_LINE_BIG_DARK from "@/assets/images/V2/banner-line_big_dark.png";
import BANNER_LINE_BIG_BLUE from "@/assets/images/V2/banner-line_big_blue.png";
import BANNER_LINE_SHORT_ORANGE from "@/assets/images/V2/banner-line_short_orange.png";
import BANNER_LINE_COMPACT from "@/assets/images/V2/banner-line_compact.png";
import { SMAdditionalInfo, SMCases, SMUser } from "@/store";
import { track } from "@amplitude/analytics-browser";
import {
  IS_NOT_SPONSOR,
  SL_IS_STARTED,
  SL_IS_STARTED_AND_ON_FINAL_STEPS,
  NEED_TO_COMPLETE_ACCOUNT_SETUP,
  COMPLIANCE_SUBSCRIPTION_EXPIRED,
  TRIAL_IS_ENDING_SOON,
  UPGRADE_TO_PREMIUM,
  HOME_OFFICE_AUDITS,
  SL_GRANTED,
  ONGOING_RESPONSIBILITIES,
  // Visa Cases
  SL_ONBOARDING,
  NO_SL,
  SL_IN_PROGRESS,
  SL_ON_REVIEW,
} from "@/constants/banner";

const CLASS = "banner";

const ICONS = {
  close: "close-outline",
  information: "information-circle",
};

const PRICING_ROUTE = "Pricing";

@Component
export default class Banner extends Vue {
  @Prop({ type: Object, default: () => ({}) }) readonly src;
  @Prop({ type: Boolean, default: false }) readonly short: boolean;
  @Prop({ type: String, default: null }) readonly type: string;

  BANNER_LINE_SMALL = BANNER_LINE_SMALL;
  BANNER_LINE_BIG_SHORT = BANNER_LINE_BIG_SHORT;
  BANNER_LINE_BIG_SHORT_DARK = BANNER_LINE_BIG_SHORT_DARK;
  BANNER_LINE_BIG = BANNER_LINE_BIG;
  BANNER_LINE_BIG_DARK = BANNER_LINE_BIG_DARK;
  BANNER_LINE_BIG_BLUE = BANNER_LINE_BIG_BLUE;
  BANNER_LINE_SHORT_ORANGE = BANNER_LINE_SHORT_ORANGE;
  BANNER_LINE_COMPACT = BANNER_LINE_COMPACT;

  ICONS = ICONS;

  get tasksCount() {
    return this.src?.banner?.compliance_admin_overdue_task_count || 0;
  }

  get routeQuery() {
    return this.$route.query;
  }

  get selectJobPositionName() {
    if (!this.routeQuery?.name) return "";

    return `${this.routeQuery.name}'s`;
  }

  get dataset() {
    return {
      empty: {
        title: "",
        buttonLabel: "",
        props: {},
        action: null,
      },

      // Banners

      [SL_GRANTED]: {
        title: "You’re Set Up for What’s Next",
        text:
          "Your Sponsor Licence has been granted - great news. As soon as you start the visa process for your first candidate, new compliance obligations will begin.\n" +
          `<b>Good news:</b> your compliance subscription is already active and ready to support you when that time comes.\n`,
        buttonLabel: "See What’s Covered",
        props: {
          long: true,
          blue: true,
        },
        action: this.goToPricing,
      },
      [ONGOING_RESPONSIBILITIES]: {
        title: "Ongoing Responsibilities Have Now Started",
        text:
          "Congratulations - you’ve officially onboarded your first team member who requires immigration compliance.\n" +
          "This marks a key milestone, but it also means your legal obligations as a sponsor have now begun.\n" +
          `Your subscription includes support for this stage and will remain active until <b>${SMUser.complianceExpitationDate}</b>.\n`,
        buttonLabel: "Review Your Compliance Tasks",
        props: {
          short: true,
        },
        action: this.goToComplianceTasks,
      },

      // [COMPLIANCE_SUBSCRIPTION_EXPIRED]: {
      //   title: "Your Compliance Subscription has Expired",
      //   text:
      //     "Since last April, Home Office checks have increased - even for existing sponsors. Without support, you risk serious penalties.\n" +
      //     "Stay protected with expert support. Our Compliance Packages keep you audit-ready and fully covered.\n",
      //   buttonLabel: "Reactivate Your Compliance Subscription",
      //   props: {
      //     short: true,
      //   },
      //   action: this.goToPricing,
      // },
      [COMPLIANCE_SUBSCRIPTION_EXPIRED]: {
        title: `You have ${
          this.tasksCount ? this.tasksCount : ""
        } pending compliance tasks that require immediate attention.`,
        text:
          "You risk missing critical deadlines, facing fines, or losing your sponsor licence. Renew the subscription to regain access and stay compliant.",
        buttonLabel: "Choose your plan to stay covered",
        props: {
          short: true,
        },
        action: this.goToPricing,
      },
      [TRIAL_IS_ENDING_SOON]: {
        title: "Your Free Compliance Trial is Ending Soon",
        text:
          "Essential compliance features will soon be locked. Stay protected with expert support. Our Compliance Packages keep you audit-ready and fully covered.",
        buttonLabel: "Choose Your Plan to Stay Covered",
        props: {
          short: true,
        },
        action: this.goToPricing,
      },
      [UPGRADE_TO_PREMIUM]: {
        title: "Upgrade to Premium Subscription - We'll Handle All Your Immigration Compliance",
        text:
          "Stay compliant without the stress. Our experts handle everything - Home Office communication, full data reviews, and proactive monitoring - so you don’t have to. Avoid penalties and focus on your business with complete peace of mind.",
        buttonLabel: "Get Premium Subscription",
        props: {
          short: true,
        },
        action: this.goToPricing,
      },
      [HOME_OFFICE_AUDITS]: {
        title: "Home Office Audits",
        text:
          "Home Office audits are strict - without proper preparation, you risk penalties or even losing your sponsor licence. Our Compliance Audit Service helps you pass with confidence by identifying risks before the Home Office does.",
        buttonLabel: "Get a Mock Audit from Our Experts",
        props: {
          short: true,
          dark: true,
        },
        action: this.openMockAuditPopup,
      },

      // Banners Action

      [NEED_TO_COMPLETE_ACCOUNT_SETUP]: {
        title: "Complete your account setup to unlock all platform features",
        buttonLabel: "Start",
        props: {
          small: true,
        },
        action: this.initUserOnboarding,
      },
      [IS_NOT_SPONSOR]: {
        title:
          "To hire international employees, you’ll first need a Sponsor Licence. Start your application now.",
        buttonLabel: "Apply",
        props: {
          small: true,
        },
        action: this.startSLOpenPopup,
      },
      [SL_IS_STARTED]: {
        title:
          "You're nearly there! Complete your Sponsor Licence Application to start hiring international talent.",
        buttonLabel: "Continue",
        props: {
          small: true,
        },
        action: this.goToSLCase,
      },
      [SL_IS_STARTED_AND_ON_FINAL_STEPS]: {
        title:
          "Your Sponsor Licence application is being processed. Click 'View' to check the status and details.",
        buttonLabel: "View",
        props: {
          small: true,
        },
        action: this.goToSLCase,
      },

      // Banner Extension
      select_job_position: {
        title: `Select a job position for ${this.selectJobPositionName} Skilled Worker Visa extension`,
        text: `You can choose from the list or create a new one. Then click <b>'Start'</b> to proceed.`,
        props: {
          small: true,
        },
      },

      // Banner Visa Cases
      [SL_ONBOARDING]: {
        title:
          "You’ll be able to start Visa applications once you’ve completed your account set up",
        buttonLabel: "Start",
        props: {
          small: true,
        },
        action: this.initUserOnboarding,
      },
      [NO_SL]: {
        title:
          "You’ll be able to start Visa applications once you’ve obtained your Sponsor Licence",
        text: "Start your Sponsor Licence application now to unlock all features",
        buttonLabel: "Start",
        props: {
          small: true,
        },
        action: this.startSLOpenPopup,
      },
      [SL_IN_PROGRESS]: {
        title:
          "You’ll be able to start Visa applications once you’ve obtained your Sponsor Licence",
        text: "Continue your application now to unlock all features.",
        buttonLabel: "Continue",
        props: {
          small: true,
        },
        action: this.goToSLCase,
      },
      [SL_ON_REVIEW]: {
        title:
          "Your Sponsor Licence application is being processed. Click ‘View’ to check the status and details.",
        text: "Visa applications will unlock as soon as you obtain your Sponsor Licence",
        buttonLabel: "View",
        props: {
          small: true,
        },
        action: this.goToSLCase,
      },

      // Employer empoyees
      employee_less_than_4_months: {
        title: "Less than 4 months until an employee’s visa expires?",
        text:
          "Click ‘<b>Extend Visa</b>’ to view eligible employees and start the extension process.",
        buttonLabel: "Extend Visa",
        props: {
          small: true,
        },
        action: this.startSWExtend,
      },
      employee_expired: {
        title: "Your Compliance subscription has expired",
        text:
          "Activate your Compliance plan to meet Home Office requirements and avoid penalties or sponsorship restrictions",
        buttonLabel: "Activate now",
        props: {
          short: true,
          compact: true,
          orange: true,
        },
        action: this.goToPricing,
      },
      employee_expired_less_than_4_months: {
        title: "Less than 4 months until an employee’s visa expires?",
        text:
          "Click ‘<b>Extend Visa</b>’ to view eligible employees and start the extension process.",
        buttonLabel: "Extend Visa",
        props: {
          short: true,
          compact: true,
        },
        action: this.startSWExtend,
      },
    };
  }

  get showBannerLineSmallLight() {
    const { props } = this.content;

    if (!props) return false;

    if (props.orange) return false;
    if (props.compact) return false;

    if (!props.small || props.short) return false;

    return props.small === true && props.dark !== true && props.blue !== true;
  }

  get showBannerLineBigShort() {
    const { props } = this.content;

    if (!props) return false;

    if (!props.small || !props.short) return false;

    return props.small !== true && props.dark !== true && props.blue !== true;
  }

  get showBannerLineBigShortDark() {
    const { props } = this.content;

    if (!props) return false;

    if (!props.short) return false;

    return props.small !== true && props.dark === true;
  }

  get showBannerLineBig() {
    const { props } = this.content;

    if (!props) return false;

    if (props.compact) return false;
    if (props.orange) return false;

    return props.small !== true && props.dark !== true && props.blue !== true;
  }

  get showBannerLineBigDark() {
    const { props } = this.content;

    if (!props) return false;

    if (props.dark) return false;

    return props.small !== true && props.dark === true && props.blue !== true;
  }

  get showBannerLineBigBlue() {
    const { props } = this.content;

    if (!props) return false;

    return props.blue === true;
  }

  get showBannerLineShortOrange() {
    const { props } = this.content;

    if (!props) return false;

    return props.orange === true;
  }

  get showBannerLineCompact() {
    const { props } = this.content;

    if (!props) return false;

    if (props.orange) return false;

    return props.compact === true;
  }

  get showReadTime() {
    return !!this.content?.readTime;
  }

  get content(): any {
    return this.type && this.dataset[this.type] ? this.dataset[this.type] : this.dataset.empty;
  }

  get classes() {
    const { props } = this.content;

    return {
      [`${CLASS}_small`]: props?.small === true,
      [`${CLASS}_short`]: props?.short === true,
      [`${CLASS}_long`]: props?.long === true,
      [`${CLASS}_dark`]: props?.dark === true,
      [`${CLASS}_blue`]: props?.blue === true,
      [`${CLASS}_orange`]: props?.orange === true,
      [`${CLASS}_compact`]: props?.compact === true,
    };
  }

  get isDarkMode() {
    return this.content?.props?.dark === true;
  }

  get buttonProps() {
    return {
      medium: true,
      secondary: this.content?.props?.dark === true,
      error: this.content?.props?.orange === true,
    };
  }

  get readTimeLabel() {
    const readTime = this.content?.readTime;

    if (!readTime) return null;

    return `~ ${readTime}min`;
  }
  get buttonLabel() {
    return this.content?.buttonLabel ?? "";
  }

  get title() {
    return this.content?.title ?? "";
  }

  get text() {
    return this.content?.text ?? "";
  }

  get action() {
    return alert("ACTION!");
  }

  emitAction(type: string, buttonLabel) {
    this.content?.action?.();

    track("Clicked CTA", { type, buttonLabel });
  }

  startSW() {
    SMAdditionalInfo.startSWQuickAction();
  }

  openMockAuditPopup() {
    SMAdditionalInfo.mockAuditQuickAction();
  }

  async goToPricing() {
    try {
      await this.$router.push({
        name: PRICING_ROUTE,
      });
    } catch {
      return;
    }
  }

  initUserOnboarding() {
    SMAdditionalInfo.initUserOnboarding();
  }

  get slCaseId() {
    return SMUser.user?.sl_case_id ?? 0;
  }

  async goToSLCase() {
    const { slCaseId } = this;

    if (!slCaseId) return;

    await this.$router.push(`/cases/${slCaseId}/dashboard/${SMCases.tabId}`);
  }

  async goToComplianceTasks() {
    await this.$router.push(`/employer/tasks`);
  }

  startSLOpenPopup() {
    this.$root.$emit("open-start-sl-popup");
  }

  async startSWExtend() {
    await this.$router.push("/employer/services/extension");
  }
}
